import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { selectCourseContent, selectCourseStructure, selectCourseType } from '../../../pages/courses/create-courses/store/course.selector';
import { CourseContent, CourseStructureNode, Type } from '../../../pages/courses/create-courses/store/course.model';
import { Observable, Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { CourseService } from '../../../pages/courses/course.service';
import { CoursePreviewRefreshService } from '../../../providers/course-preview-refresh.service';

interface PreviewStructureItem {
  label: string;
  level: number;
  type: 'node' | 'video' | 'flash';
}

@Component({
  selector: 'app-device-preview',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './device-preview.component.html',
  styleUrl: './device-preview.component.css'
})
export class DevicePreviewComponent implements OnInit, OnDestroy {
  courseType$!: Observable<Type | null>;
  courseData$!: Observable<CourseContent | null>;
  courseStructure$!: Observable<any>;

  tabs: string[] = ['details', 'structure', 'review'];
  currentTab: string = this.tabs[0];

  courseDetails: any = null;
  reviewsList: any[] = [];
  structureList: PreviewStructureItem[] = [];

  private destroy$ = new Subject<void>();
  private loadedCourseId: string | number | null = null;
  private lastCourseUpdatedAt: string | null = null;

  readonly ratingStars = [1, 2, 3, 4, 5];

  constructor(
    private store: Store,
    private courseApiService: CourseService,
    private previewRefresh: CoursePreviewRefreshService
  ) {
    this.courseType$ = this.store.select(selectCourseType);
    this.courseData$ = this.store.select(selectCourseContent);
    this.courseStructure$ = this.store.select(selectCourseStructure);
  }

  ngOnInit(): void {
    this.courseStructure$
      .pipe(takeUntil(this.destroy$))
      .subscribe((structure) => {
        const nodes = structure?.data ?? [];
        this.structureList = Array.isArray(nodes) ? this.buildStructurePreview(nodes) : [];
      });

    this.courseData$
      .pipe(takeUntil(this.destroy$))
      .subscribe((course) => {
        if (!course?.id) return;
        const updatedAt = course.updated_at ?? null;
        if (updatedAt && updatedAt !== this.lastCourseUpdatedAt) {
          this.lastCourseUpdatedAt = updatedAt;
          this.loadDetails(course.id);
          this.loadReviews(course.id);
          this.loadStructure(course.id);
        }
      });

    this.courseType$
      .pipe(takeUntil(this.destroy$))
      .subscribe((type) => {
        const id = type?.id;
        if (!id || this.loadedCourseId === id) return;

        this.loadedCourseId = id;
        this.loadDetails(id);
        this.loadReviews(id);
        this.loadStructure(id);
      });

    this.previewRefresh
      .onRefresh$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        if (!event?.courseId) return;
        this.loadDetails(event.courseId);
        this.loadReviews(event.courseId);
        this.loadStructure(event.courseId);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getUsefulForList(usefulForHint?: string | null, usefulFor?: string | null): string[] {
    const raw = usefulFor ?? usefulForHint ?? '';
    return raw
      .split(',')
      .map((item) => item.trim())
      .filter((item) => !!item);
  }

  parseTextList(value: string | null | undefined): string[] {
    if (!value) return [];
    return value
      .split(/\r?\n|,/)
      .map((item) => item.trim())
      .filter((item) => !!item);
  }

  getRoundedRating(value: any): number {
    const rating = Number(value || 0);
    if (Number.isNaN(rating)) return 0;
    return Math.max(0, Math.min(5, Math.round(rating)));
  }

  private loadDetails(courseId: string | number) {
    this.courseApiService.getCourseDeatils(courseId).subscribe({
      next: (res: any) => {
        if (res?.body?.status === 'success') {
          this.courseDetails = res.body.data ?? null;
        }
      },
      error: () => {
        this.courseDetails = null;
      }
    });
  }

  private loadReviews(courseId: string | number) {
    this.courseApiService.getReviews(courseId).subscribe({
      next: (res: any) => {
        if (res?.body?.status === 'success') {
          this.reviewsList = (res.body.data ?? []).map((item: any) => ({
            ...item,
            rating: Number(item.rating || 0)
          }));
        } else {
          this.reviewsList = [];
        }
      },
      error: () => {
        this.reviewsList = [];
      }
    });
  }

  private loadStructure(courseId: string | number) {
    this.courseApiService.getStructure(courseId).subscribe({
      next: (res: any) => {
        if (res?.body?.status === 'success') {
          const nodes = res.body.data ?? [];
          this.structureList = this.buildStructurePreview(nodes);
        }
      },
      error: () => {
        this.structureList = [];
      }
    });
  }

  private buildStructurePreview(nodes: CourseStructureNode[]): PreviewStructureItem[] {
    const items: PreviewStructureItem[] = [];

    const walk = (node: any, level: number) => {
      items.push({
        label: node?.structure_name || 'Untitled',
        level,
        type: 'node'
      });

      (node?.structure_maps ?? []).forEach((mapItem: any) => {
        if (mapItem?.action_type === 'video' && mapItem?.video?.video_title) {
          items.push({
            label: mapItem.video.video_title,
            level: level + 1,
            type: 'video'
          });
        } else if (mapItem?.action_type === 'flash') {
          items.push({
            label: 'Flashcards',
            level: level + 1,
            type: 'flash'
          });
        }
      });

      (node?.child ?? []).forEach((childNode: any) => walk(childNode, level + 1));
      (node?.grandChild ?? []).forEach((grandNode: any) => walk(grandNode, level + 1));
    };

    (nodes ?? []).forEach((node: any) => walk(node, 0));
    return items;
  }
}
